import React from 'react';

interface DonutChartProps {
  data: {
    label: string;
    value: number;
    color: string;
  }[];
  centerLabel?: string;
  centerValue?: string;
  size?: number;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  centerLabel,
  centerValue,
  size = 200
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let cumulativePercentage = 0;
  
  const createPath = (percentage: number, offset: number) => {
    const angle = (percentage / 100) * 360;
    const startAngle = (offset / 100) * 360 - 90;
    const endAngle = startAngle + angle;
    
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const largeArcFlag = angle > 180 ? 1 : 0;
    
    const x1 = size/2 + (size/2 - 30) * Math.cos(startAngleRad);
    const y1 = size/2 + (size/2 - 30) * Math.sin(startAngleRad);
    const x2 = size/2 + (size/2 - 30) * Math.cos(endAngleRad);
    const y2 = size/2 + (size/2 - 30) * Math.sin(endAngleRad);
    
    return `M ${size/2} ${size/2} L ${x1} ${y1} A ${size/2 - 30} ${size/2 - 30} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  return (
    <div className="relative">
      <svg width={size} height={size} className="transform -rotate-90">
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const path = createPath(percentage, cumulativePercentage);
          cumulativePercentage += percentage;
          
          return (
            <path
              key={index}
              d={path}
              fill={item.color}
              className="transition-all hover:opacity-80 cursor-pointer"
            />
          );
        })}
        <circle
          cx={size/2}
          cy={size/2}
          r={size/2 - 60}
          fill="white"
        />
      </svg>
      
      {(centerLabel || centerValue) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {centerValue && (
            <span className="text-2xl font-bold text-gray-900">{centerValue}</span>
          )}
          {centerLabel && (
            <span className="text-sm text-gray-600">{centerLabel}</span>
          )}
        </div>
      )}
      
      <div className="mt-4 space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2" 
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-700">{item.label}</span>
            </div>
            <span className="font-medium text-gray-900">
              {((item.value / total) * 100).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};